import { Avatar, Box, Typography } from "@dealer-center/core-components";
import {
  useGetBridgeId,
  useGetUserInfo
} from "@dealer-center/ddc-pages-configuration-fetch";
import type { UserInfoSubject } from "./UserInfoSubject";
import {
  ON_SURFACE_INVERSE,
  ON_SURFACE_MUTED,
  SPACE_BLOCK,
  SURFACE_INTERACTIVE_REST
} from "./layoutTokens";
import { belowMobile, desktopOnly, mobileOnly } from "./responsive";

export interface IdentityHeaderProps {
  subject: UserInfoSubject;
}

/**
 * The identity's **name** below `md`: 14px, truncated to one line.
 *
 * Reviewer call, 2026-09-23, reached in three steps — the first two were wrong and are worth
 * recording, because the obvious fixes both failed:
 *
 * 1. **Shrinking the whole line to 14px did nothing for the layout.** A real identity —
 *    `AthenaExtDDCUser AthenaExtDDCUser (AthenaExtDDCUser) • Bridge ID: AthenaExtDDCUser`, ~80
 *    characters — still wrapped to **four** lines in a 372px header. Font size was never the
 *    problem; the amount of content on one line was.
 * 2. **Giving Back its own row did not help either**, because the identity still wrapped once it had
 *    the width. Only truncation bounds it.
 *
 * So the name ellipsises, which makes its line exactly one line tall for any identity.
 *
 * **The name keeps `h3`'s weight**, because only `fontSize` is overridden — weight carries the
 * hierarchy that size used to. Applied via `sx` rather than by swapping `variant`, because `variant`
 * is a prop and cannot carry a breakpoint (same reason `PageHeading` shrinks its `h1`).
 */
const IDENTITY_NAME_BASE = {
  minWidth: 0,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "normal",
  width: "auto"
};

/**
 * The name's mobile overrides, kept **separate** from its base rather than pre-wrapped.
 *
 * ⚠️ A constant built with `belowMobile(...)` cannot be safely spread beside another `belowMobile`
 * at the call site — the two emit the same media-query key and the later wins outright. Splitting
 * base from overrides lets the call site merge them into one block. See `belowMobile`'s own note;
 * this exact collision silently dropped the `nowrap` here on 2026-09-24.
 */
const IDENTITY_NAME_MOBILE = {
  fontSize: "14px",
  whiteSpace: "nowrap",
  width: "100%"
};

const IDENTITY_META_ORDER = { company: 2, metadata: 3, name: 1 };

/**
 * The identity's one wrap container, and the whole of the mobile arrangement lives here.
 *
 * Its children are the **name**, the **company** and the **metadata group** (username • Bridge ID),
 * ordered by {@link IDENTITY_META_ORDER}. `flexWrap: "wrap"` plus one `width: 100%` per line-owner
 * is what decides the line breaks, at both breakpoints:
 *
 * | | name | company | metadata |
 * |---|---|---|---|
 * | **desktop** | `auto` — shares line 1 | `100%` — breaks to line 2 | `auto` — shares line 1 |
 * | **below `md`** | `100%` — owns line 1 | `auto` — shares line 2 | `auto` — shares line 2 |
 *
 * So the same three elements give `Name (username) • Bridge ID` over `Company` at desktop, and
 * `Name` over `Company • username • Bridge ID` on a phone — with no duplicated markup and no
 * `display` tricks. **The company must be a child of this container**, not a sibling below it, or it
 * cannot join the metadata line; that is the one structural change from the shipped version.
 */
const IDENTITY_ROW = { flexWrap: "wrap" as const };

/**
 * `(username) • Bridge ID: …` — **inline beside the name at desktop, its own bottom line below `md`.**
 *
 * Reviewer call, 2026-09-23: these were briefly hidden outright on mobile (which lost the subject's
 * Bridge ID entirely), then restored as the header's **last** line — below the name, the account and
 * the action row — as **small, dark-grey** text. That ordering is deliberate: they are reference
 * values, so they read as a footnote to the identity rather than part of it.
 *
 * **12px**, a step below the 14px name and account, so the three lines form a hierarchy rather than
 * three equal rows. The grey comes from the `color` prop on each value
 * (`sem.color.on-surface.muted`) and is unchanged.
 *
 * The values inside stay inline; only their **container** moves, via `order`. That is why this
 * needs no `display: contents` gymnastics — an earlier revision flattened four levels of wrapper to
 * achieve the same thing and was replaced, because the `order` values then had to be numbered
 * against the header row and the whole arrangement broke silently if any wrapper's `display`
 * changed.
 */
const IDENTITY_META = {
  /*
   * At desktop this sits beside the name; below `md` `order` moves it after the company so the two
   * form one metadata row. See {@link IDENTITY_ROW}.
   *
   * ⚠️ **It WRAPS rather than truncating, and that is deliberate — do not "fix" it with an
   * ellipsis.** That was tried (2026-09-23): `flex: 1` + `nowrap` + `text-overflow: ellipsis` kept
   * the header to two lines, and **clipped the Bridge ID out of existence** for a wide identity —
   * `ROI Motors • AthenaExtDDCUser • Bridge ID: AthenaExtDDCUser` is ~348px at 12px in a 372px
   * header. Cypress caught it as "expected <span> to be visible", which is exactly the defect:
   * these values were restored to the mobile header *so they could be read*, so bounding the header
   * at their expense defeats the point.
   *
   * The trade accepted instead: a typical identity gives a two-line header, an unusually long one
   * gives three. The name above still truncates, because a name has no tail worth wrapping for.
   */
  /*
   * ⚠️ **A flex row with a `columnGap`, not a plain block — the gap IS the spacing around the `•`.**
   *
   * On `master` the username, the bullet and the Bridge ID were direct children of the name row and
   * took their spacing from its `columnGap: 8px`. Wrapping them in this group made them plain inline
   * spans with no whitespace between them in the JSX, so they rendered as
   * `ROI Motors•jeffersonh7•Bridge ID: …` — cramped at **both** breakpoints, a desktop regression
   * introduced along with the mobile work. Restoring the gap here restores the original spacing
   * rather than inventing a new one.
   *
   * `alignItems: "baseline"` matches the parent row, so the 12px values sit on the same baseline as
   * each other rather than being centred against the tallest.
   */
  alignItems: "baseline",
  columnGap: "8px",
  display: "flex",
  flexWrap: "wrap",
  minWidth: 0,
  order: 2,
  ...belowMobile({ order: IDENTITY_META_ORDER.metadata })
};

/**
 * The single metadata line below `md`: **company, username, Bridge ID** — one small grey row.
 *
 * Reviewer call, 2026-09-23, replacing a two-band arrangement they rejected: the company had been
 * given a full-width row of its own with the username and Bridge ID on a second one. It reads as one
 * line of reference values, so it renders as one.
 *
 * The company keeps its position (first) and loses its `strong-sm` emphasis here, because a bold
 * value leading a row of plain ones reads as a heading for them rather than a peer.
 *
 * `order` is numbered **within the identity's own row** (`IDENTITY_ROW`), which is all that is
 * needed: name first, then company, then the metadata group. The name takes the full width below
 * `md` and so claims a line of its own, leaving company and metadata to flow together on the next.
 */

/**
 * 12px on each metadata **value**, not on their wrapper — and that distinction cost a red test.
 *
 * `Typography` sets its own `font-size` from `variant` (`body-lg` is 18px), so a `fontSize` on the
 * enclosing `Box` is simply not inherited: CSS inheritance cannot override an explicit declaration
 * on the child. The wrapper owns the *layout* ({@link IDENTITY_META} — order, width, truncation) and
 * each value owns its *size*.
 *
 * The grey is unchanged and comes from each value's `color` prop
 * (`sem.color.on-surface.muted`); 12px against the 14px name and account is what makes this line
 * read as a footnote rather than a third equal row.
 */
/** The metadata values' mobile size. Split from any `display` for the same reason as the name. */
const IDENTITY_META_TEXT_MOBILE = { fontSize: "12px" };

/**
 * FR-004's identity header: avatar, name, username, Bridge ID, account. The card's first row.
 *
 * **The Bridge ID line is CC6 parity** (reviewer request, 2026-09-17): CC6 renders
 * `Ape Zone665 (apezone665) • Bridge ID: BRIDGEapezone665`, and this reproduces it. Two things about
 * it are easy to get wrong, both covered at `useGetBridgeId`: the value is CC9's `username` from
 * `/user-profile/{ccUsername}` and **not** `UserDetail.platformId` (a GUID, usually null), and it is
 * keyed on `subject.userId` rather than taken from `ccUserInfo.bridge_user_name`, which reports the
 * operator while impersonating. So it always describes the person this header names.
 *
 * **Both the name and the account line come from `useGetUserInfo().data.summary`, not from
 * `useMyInfoSubject`** — see the note beside each below. For the account line (Notes → gap 1): the
 * outlet context carries `accountId` but no account name, so the subject hook deliberately exposes
 * none — the only value it could return is the id, which would then be rendered as if it were the
 * name. Calling `useGetUserInfo` here costs no second request: React Query dedupes it against the
 * User Information tab's identical query key.
 *
 * Known limitation, accepted: `company` is a per-user, user-editable profile field, so it can
 * diverge from the account's real name (of 12 sampled `dealerdemo` users, `jlwood2420` read
 * "Marketing Team"). Still better than rendering the raw account id; the durable fix is a BFF
 * endpoint over CC9 `/serviceAccounts/{accountId}`, out of scope here.
 *
 * FR-003's `h1` is **not** here — it's page furniture outside the card, in `PageHeading`.
 */
export const IdentityHeader = ({ subject }: IdentityHeaderProps) => {
  const { data } = useGetUserInfo({
    userId: subject.userId,
    accountId: subject.accountId
  });

  const summary = data?.summary;

  const company = summary?.company?.trim();
  const accountLine = company ? company : subject.accountId;

  /*
   * A second query, and it is cheap: React Query dedupes it against nothing else on the page (no tab
   * reads it), and it is keyed and cached per user for five minutes like every other read here.
   */
  const { data: bridge } = useGetBridgeId({ userId: subject.userId });
  const bridgeId = bridge?.bridgeId;

  /*
   * The name comes from the **profile record**, not from `subject.displayName`, for the same reason
   * the account line comes from `summary.company`: it is the value this page is about.
   *
   * `subject.displayName` is built from the outlet context's `first`/`last`, which the ControlCenter
   * lambda copies straight off the **session JWT** — it describes whoever is signed in. In an "as"
   * context the lambda swaps `userId`/`accountId` to the as-user but passes `first`/`last` through
   * untouched, so the header rendered the operator's name above the as-user's record — and directly
   * above the First Name / Last Name fields holding the *other* name. Reading `summary` makes the
   * header agree with the fields it sits on top of, by construction.
   *
   * Falls back to `subject.displayName` while the query is pending and when the record carries
   * neither name (CC9 returns `""`, not null, for unset strings) — so the header is never blank.
   */
  const profileName = [summary?.firstName, summary?.lastName]
    .filter(part => Boolean(part?.trim()))
    .join(" ")
    .trim();
  const displayName = profileName || subject.displayName;

  /*
   * The avatar is decorative: it carries the name's first letter and no information the adjacent
   * text does not already state, so it is hidden from assistive technology rather than announced as
   * a redundant single character.
   */
  const initial = displayName.trim().charAt(0).toUpperCase();

  return (
    <Box
      sx={{
        alignItems: "center",
        columnGap: SPACE_BLOCK,
        display: "flex",
        /*
         * ⚠️ **`minWidth: 0` here and on the text column below is what makes truncation possible.**
         * A flex item defaults to `min-width: auto`, which refuses to shrink below its content — so
         * without these two the ellipsis never engages and the identity pushes the header wider
         * instead. Same trap `UserRow`'s `TRUNCATE` documents.
         */
        minWidth: 0,
        rowGap: "8px",
        ...belowMobile({ columnGap: "8px" })
      }}
    >
      {/*
       * `sizes="lg"` is Interstate's largest step (48px). The prototype draws 56px, which the scale
       * has no step for — 48px is taken over an `sx` width/height so the avatar stays on the design
       * system's scale; the 8px is imperceptible beside the name.
       *
       * The **fill**, though, has to be overridden, and the nested selector is load-bearing.
       * Interstate styles its own avatar with a self-referencing `.css-<hash>.MuiAvatar-root` rule —
       * two classes, so specificity (0,2,0) — which silently beats a plain `sx` object at (0,1,0):
       * the avatar rendered as the default grey `#e3e3e3` with black text and the `sx` colours were
       * dropped on the floor with no warning. `&.MuiAvatar-root` matches that specificity so the
       * approved blue actually applies.
       *
       * This is the one place in the frame that reaches past a component's public API, so it is the
       * one place an Interstate upgrade could regress — if the avatar ever renders grey again, this
       * selector is why.
       *
       * ⚠️ **`aria-hidden` MUST go on this wrapper, not on `Avatar`** — and it used to be on
       * `Avatar`, where it did nothing. Interstate's `Avatar` destructures its known props and
       * forwards only `data-testid` and `sizes` out of `rest`, so the attribute never reached the
       * DOM and the initial *was* announced: a bare "A" read out above the name it is a letter of.
       * `UserRow` has carried the wrapper workaround (and a comment naming this file's copy of the
       * defect) since 2026-09-14; fixed here 2026-09-23 under TA4236901. Do not "simplify" the
       * wrapper away.
       *
       * **Desktop only (TA4236901).** The avatar renders one letter of the adjacent name and
       * nothing else — the payload's `picture` field is rendered by nothing — so below the
       * breakpoint its ~64px goes to the name and the action cluster, which is the row that has to
       * fit a back affordance, an overflow menu and Save at 390px.
       */}
      <Box aria-hidden={"true"} sx={{ flexShrink: 0, ...desktopOnly("block") }}>
        <Avatar
          data-testid={"myinfo-avatar"}
          sizes={"lg"}
          sx={{
            "&.MuiAvatar-root": {
              backgroundColor: SURFACE_INTERACTIVE_REST,
              color: ON_SURFACE_INVERSE
            }
          }}
        >
          {initial}
        </Avatar>
      </Box>

      <Box sx={{ minWidth: 0, width: "100%" }}>
        <Box
          sx={{
            alignItems: "baseline",
            columnGap: "8px",
            display: "flex",
            /*
             * ⚠️ **Wrap at BOTH breakpoints — see {@link IDENTITY_ROW}.** This line briefly carried a
             * second, conflicting `flexWrap: responsive("nowrap", "wrap")` *after* the spread, which
             * won and made the mobile container `nowrap`: the name (at `width: 100%`), the company
             * and the metadata were all forced onto one overflowing line. It survived because the
             * Cypress assertions only compared tops with a ±4px tolerance, which a single line
             * satisfies — they now require the company to start **below the name's box**.
             */
            ...IDENTITY_ROW,
            minWidth: 0
          }}
        >
          <Typography
            data-testid={"myinfo-display-name"}
            sx={{
              ...IDENTITY_NAME_BASE,
              order: IDENTITY_META_ORDER.name,
              /*
               * ⚠️ **`width: 100%`, never `flex: 1`.** With `nowrap` + `overflow: hidden` a
               * `flex: 1` item takes `flex-basis: 0` and collapses to **zero width** in a wrapping
               * container — measured at `w=0`, which silently broke the whole row's layout. An
               * explicit width both claims line one and gives the ellipsis something to bound.
               */
              ...belowMobile(IDENTITY_NAME_MOBILE)
            }}
            tag={"p"}
            variant={"h3"}
          >
            {displayName}
          </Typography>
          {/*
           * `tag="span"` with an `h3`-scaled sibling: the username sits on the same line at the same
           * size but unemphasised, which is the prototype's `.uid` treatment. It is not a heading and
           * must not become one — the page's outline is `h1` → the tabs' section headings, and an
           * `h3` element here would wedge a level in above them.
           */}
          {/*
           * `(username) • Bridge ID: …` in **one wrapper**, so the three values travel together when
           * the mobile layout moves them to the header's **last** line. See `IDENTITY_META`.
           */}
          <Box sx={IDENTITY_META}>
            {/*
             * Mobile-only separator, because the company only shares this line below `md`. Furniture
             * rather than copy — the same call the `•` between username and Bridge ID makes — so it
             * is `aria-hidden` and carries no label key.
             */}
            <Typography
              aria-hidden={"true"}
              color={"sem.color.on-surface.muted"}
              data-testid={"myinfo-company-separator"}
              sx={{
                ...mobileOnly("inline", IDENTITY_META_TEXT_MOBILE)
              }}
              tag={"span"}
              variant={"body-lg"}
            >
              {"•"}
            </Typography>
            <Typography
              color={"sem.color.on-surface.muted"}
              data-testid={"myinfo-username"}
              sx={belowMobile(IDENTITY_META_TEXT_MOBILE)}
              tag={"span"}
              variant={"body-lg"}
            >
              {subject.username}
            </Typography>
            {/*
             * **Rendered only when there is one, separator included.** A user who has never registered
             * a Bridge ID is an ordinary case, not a failure (`useGetBridgeId` resolves such a read to
             * `null` rather than erroring), and so is the moment before the query settles. Neither may
             * leave a dangling "•" or a bare "Bridge ID:" label on the row — FR-019 requires three
             * values here and this is not one of them.
             *
             * The `•` is furniture, not copy: it is structure between two values, the same call the
             * parentheses around the username above make, and `PermissionGroup`'s row count makes for
             * a numeral.
             *
             * ⚠️ **"Bridge ID" is a LITERAL here** — a deliberate exception to Configuration
             * Principle VI ("Localized Labels — No Hardcoded UI Text"), taken by reviewer decision on
             * 2026-09-18 because it is a **Cox product name** that would only ever have been
             * registered do-not-translate, i.e. three locale entries holding the same string. See the
             * fuller note at `PageHeading`, including why reinstating an *unregistered* key would be
             * worse than either option.
             */}
            {bridgeId ? (
              <>
                <Typography
                  aria-hidden={"true"}
                  color={"sem.color.on-surface.muted"}
                  data-testid={"myinfo-bridge-separator"}
                  sx={belowMobile(IDENTITY_META_TEXT_MOBILE)}
                  tag={"span"}
                  variant={"body-lg"}
                >
                  {"•"}
                </Typography>
                <Typography
                  color={"sem.color.on-surface.muted"}
                  data-testid={"myinfo-bridge-id"}
                  sx={belowMobile(IDENTITY_META_TEXT_MOBILE)}
                  tag={"span"}
                  variant={"body-lg"}
                >
                  {`Bridge ID: ${bridgeId}`}
                </Typography>
              </>
            ) : null}
          </Box>
          {/*
           * **The company lives inside this row, not below it** — that is what lets it share the
           * metadata line on a phone. At desktop `width: 100%` breaks it onto its own line, which is
           * exactly where it has always rendered, so the shipped desktop layout is unchanged.
           */}
          <Typography
            data-testid={"myinfo-account-line"}
            sx={{
              /*
               * Grey and 12px **below `md` only** — at desktop it keeps `strong-sm`'s weight and
               * default colour. Set through `sx` rather than the `color` prop, which cannot carry a
               * breakpoint.
               */
              order: IDENTITY_META_ORDER.company,
              width: "100%",
              ...belowMobile({
                color: ON_SURFACE_MUTED,
                fontSize: "12px",
                width: "auto"
              })
            }}
            tag={"p"}
            variant={"strong-sm"}
          >
            {accountLine}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
